import React, { useState } from 'react';
import { Alert, Col, Input, Modal, Row, Select } from 'antd';
import { useParams } from 'react-router-dom';
import { TemplatesPOSTRequest } from '../../../interfaces/backend/TemplatesPOSTRequest';
import stripDisallowedIdCharacters from '../../../utils/stripDisallowedIdCharacters';
import { AxiosError } from 'axios';
import DefaultTemplates from '../DefaultTemplates';
import { Channels } from '../../common/channelsEnum';
import { getChannelReadableName } from '../../../utils/readable';

type Props = {
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  createTemplate: (
    channel: string,
    params: TemplatesPOSTRequest
  ) => Promise<void>;
};

const TemplateCreationModal = (props: Props): JSX.Element => {
  const { notificationId } = useParams<{ notificationId: string }>();
  const [newTemplateId, setNewTemplateId] = useState('');
  const [newTemplateRunning, setNewTemplateRunning] = useState(false);
  const [newTemplateError, setNewTemplateError] = useState('');
  const [newTemplateChannel, setNewTemplateChannel] = useState<Channels>(
    Channels.EMAIL
  );

  const submitHandler = async () => {
    setNewTemplateRunning(true);
    try {
      const requestBody = {
        ...DefaultTemplates[newTemplateChannel],
        templateId: newTemplateId
      } as unknown as TemplatesPOSTRequest;
      await props.createTemplate(newTemplateChannel, requestBody);
      setNewTemplateId('');
      setNewTemplateError('');
      setNewTemplateRunning(false);
      props.setOpen(false);
    } catch (e) {
      setNewTemplateRunning(false);
      const error = e as AxiosError;
      if (
        error.response?.data.message &&
        error.response?.data.message.includes('duplicate templateId')
      ) {
        setNewTemplateError('There is already a template with this ID.');
      } else {
        throw e;
      }
    }
  };

  return (
    <Modal
      title={`Creating template for ${notificationId}`}
      open={props.open}
      onOk={submitHandler}
      onCancel={() => {
        props.setOpen(false);
      }}
      okButtonProps={{
        disabled: !newTemplateId,
        loading: newTemplateRunning
      }}
    >
      <Row gutter={[4, 4]} align="middle">
        <Col>Channel:</Col>
        <Col>
          <Select
            defaultValue={{
              label: getChannelReadableName(newTemplateChannel),
              value: newTemplateChannel
            }}
            style={{ width: '150px' }}
            onChange={(value) => {
              setNewTemplateChannel(value as unknown as Channels);
            }}
            options={Object.keys(Channels).map((c) => {
              return { label: getChannelReadableName(c), value: c };
            })}
          />
        </Col>
      </Row>
      <Row gutter={[4, 4]} style={{ marginTop: '16px' }}>
        <Col>New Template ID (cannot be changed later):</Col>
        <Col span={24}>
          <Input
            value={newTemplateId}
            placeholder="e.g. nike_spanish"
            onChange={(e) =>
              setNewTemplateId(stripDisallowedIdCharacters(e.target.value))
            }
            onPressEnter={submitHandler}
          />
        </Col>
      </Row>

      {newTemplateError && (
        <Alert
          style={{ marginTop: '8px' }}
          message={newTemplateError}
          type="error"
        />
      )}
    </Modal>
  );
};

export default TemplateCreationModal;
